import { fieldToDatabase } from '@databases/collab/convert/to-database';
import { type MutableDatabase } from '@databases/model/database';
import { FieldType } from '@databases/model/field';
import {
  type DatabaseField,
  type DatabaseFieldType,
  type NumberFormat
} from '@databases/types/database';
// FIXME
// eslint-disable-next-line no-restricted-imports
import { DatabaseChangeType } from '@databases/types/redux';
// FIXME
// eslint-disable-next-line no-restricted-imports
import { pickNewColor } from '@databases/utils/database/field-types/select';
// FIXME
// eslint-disable-next-line no-restricted-imports
// FIXME
// eslint-disable-next-line no-restricted-imports
import { addChangeToYjs } from '@databases/utils/redux/redux-to-yjs/add-change-to-yjs';
import { generateId } from '@databases/utils/utils';

export class FieldAgent {
  private database: MutableDatabase;

  constructor(database: MutableDatabase) {
    this.database = database;
  }

  public addField(field: DatabaseField, index?: number): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.ADD_FIELD,
        field,
        index
      },
      undefined
    );
  }

  public setFieldName(fieldName: string, newName: string): void {
    const fieldId = this.getFieldIdByName(fieldName);

    if (!fieldId) {
      throw new Error(`Field with name ${fieldName} not found`);
    }

    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.SET_FIELD_NAME,
        fieldId,
        name: newName,
        delta: [
          { delete: fieldName.length },
          {
            insert: newName
          }
        ]
      },
      undefined
    );
  }

  public setFieldType(fieldName: string, type: DatabaseFieldType): void {
    const fieldId = this.getFieldIdByName(fieldName);

    if (!fieldId) {
      throw new Error(`Field with name ${fieldName} not found`);
    }

    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.SET_FIELD_TYPE,
        fieldId,
        fieldType: type
      },
      undefined
    );
  }

  public setFieldMultiple(fieldName: string, multiple: boolean): void {
    const fieldId = this.getFieldIdByName(fieldName);

    if (!fieldId) {
      throw new Error(`Field with name ${fieldName} not found`);
    }

    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.SET_IS_MULTI,
        fieldId,
        isMulti: multiple
      },
      undefined
    );
  }

  public setFieldDescription(fieldName: string, description: string): void {
    const fieldId = this.getFieldIdByName(fieldName);
    if (!fieldId) {
      throw new Error(`Field with name ${fieldName} not found`);
    }

    const field = this.database.fieldsById[fieldId];

    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.SET_SHOW_FIELD_DESCRIPTION,
        fieldId,
        showDescription: true
      },
      undefined
    );

    const currentDescription = field.description;
    const delta =
      currentDescription === undefined || currentDescription === ''
        ? [{ insert: description }]
        : [{ delete: currentDescription.length }, { insert: description }];

    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.SET_FIELD_DESCRIPTION,
        fieldId,
        description,
        delta
      },
      undefined
    );
  }

  public setDefaultEnabled(fieldName: string, defaultEnabled: boolean): void {
    const fieldId = this.getFieldIdByName(fieldName);
    if (!fieldId) {
      throw new Error(`Field with name ${fieldName} not found`);
    }

    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.SET_DEFAULT_ENABLED,
        fieldId,
        defaultEnabled
      },
      undefined
    );
  }

  public removeField(fieldId: string): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.DELETE_FIELD,
        fieldId
      },
      undefined
    );
  }

  public getCloudId(fieldId: string): string | undefined {
    return this.database.fieldsById[fieldId].cloudId;
  }

  public setCloudId(fieldId: string, cloudId: string): void {
    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.SET_CLOUD_ID,
        fieldId,
        cloudId
      },
      undefined
    );
  }

  public setDependentFieldId(fieldName: string, dependentFieldId: string): void {
    const fieldId = this.getFieldIdByName(fieldName);
    if (!fieldId) {
      throw new Error(`Field with name ${fieldName} not found`);
    }

    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.SET_FIELD_ID,
        fieldId,
        settingsFieldId: dependentFieldId
      },
      undefined
    );
  }

  public setFieldNumberFormat(fieldName: string, numberFormat: NumberFormat): void {
    const fieldId = this.getFieldIdByName(fieldName);
    if (!fieldId) {
      throw new Error(`Field with name ${fieldName} not found`);
    }

    addChangeToYjs(
      this.database,
      {
        type: DatabaseChangeType.SET_FIELD_SETTINGS_NUMBER_FORMAT,
        fieldId,
        newValue: numberFormat
      },
      undefined
    );

    if (numberFormat.prefix !== undefined) {
      addChangeToYjs(
        this.database,
        {
          type: DatabaseChangeType.SET_FIELD_SETTINGS_PREFIX_OR_SUFFIX_FIELD,
          fieldId,
          changeType: 'prefix',
          newValue: numberFormat.prefix
        },
        undefined
      );
    }

    if (numberFormat.suffix !== undefined) {
      addChangeToYjs(
        this.database,
        {
          type: DatabaseChangeType.SET_FIELD_SETTINGS_PREFIX_OR_SUFFIX_FIELD,
          fieldId,
          changeType: 'suffix',
          newValue: numberFormat.suffix
        },
        undefined
      );
    }
  }

  public getFieldIdByName(name: string): string | undefined {
    return this.database.fields.find((fieldId) => {
      const field = this.database.fieldsById[fieldId];
      return field?.name === name;
    });
  }

  public getFieldById(fieldId: string): DatabaseField | undefined {
    const field = this.database.fieldsById[fieldId];
    if (!field) {
      return undefined;
    }

    return fieldToDatabase(fieldId, field.toJSON());
  }

  public findExistingSingleSelectField(): DatabaseField | undefined {
    const fieldIds = this.database.fields;

    for (const fieldId of fieldIds) {
      const field = this.getFieldById(fieldId);
      if (field && field.type === 'select' && !field.settings.multi) {
        return field;
      }
    }

    return undefined;
  }

  public getOrCreateSelectOption(fieldId: string, optionValue: string): string {
    const field = this.database.fieldsById[fieldId];
    if (!field) {
      throw new Error(`Field with ID ${fieldId} not found`);
    }

    if (field.type !== FieldType.Select) {
      throw new Error(`Field ${fieldId} is not a select field`);
    }

    const existingOption = field.selectOptions.options.find(
      (option) => option.label === optionValue
    );

    if (existingOption) {
      return existingOption.id;
    }

    const color = pickNewColor(field.selectOptions.options);

    const optionId = generateId();
    field.selectOptions.add({
      id: optionId,
      label: optionValue,
      color
    });

    return optionId;
  }
}
